// src/Context/AuthContext.js
import React, { createContext, useState, useContext, useEffect, useRef, useCallback, useMemo } from "react";
import { jwtDecode } from "jwt-decode";
import { getUsersId } from "../apis/MyFPO";
import { refreshToken, logoutApi, authToken } from "../apis/authApi";
import { getPendingSetupUsers } from "../apis/userTypeApi";
import servicesConfig from "../config/ServicesConfig";
import { SERVICE_ENTITY_REGISTRY, isGenuineEntityField } from "../config/serviceEntityRegistry";
import { getSubscriptionExpiryDateObject } from "../Utils/subscriptionUtils";
import { parseAccessibilityRule } from "../Utils/accessibilityRules";
import { askOtherTabsForSession, onOtherTabLogout, onOtherTabTokens, shareLogout, shareTokens } from "../Utils/tabSession";

const AuthContext = createContext();

// Mirrors feed's AccessEvaluationServiceImpl.MEMBER_HOME_SERVICE - the one service each member
// usertype keeps evaluating against its own tenant (FPO_MEMBER -> MY_ORG, BUSINESS_MEMBER ->
// MY_BUSINESS), used below only to resolve a member's OWN home context, since neither member
// usertype code ever appears as a SERVICE_ENTITY_REGISTRY homeUserTypeCode (those only list the
// admin-tier INSTITUTIONAL/INDIVIDUAL codes).
const MEMBER_HOME_SERVICE_KEY = { FPO_MEMBER: "MY_ORG", BUSINESS_MEMBER: "MY_BUSINESS" };

// Accessibility tiers that put an entity-owning service in scope for the entity-setup check
// (AUTH_LOGIN_FLOW_PLAN.md §3-§6 update). HIDDEN and SUBSCRIBE_REQUIRED are excluded — a service
// the user can't currently reach shouldn't force them into its setup flow. READ_ONLY is also
// excluded (confirmed live, 2026-10-15): it used to be treated the same as FULL, funneling a
// cross-usertype account (e.g. an FPO account with MY_BUSINESS pinned to READ_ONLY) through the
// full "Setup Required" entity form, then landing on the exact same full interactive dashboard
// as a genuine FULL-access account once submitted — nothing in the dashboard actually
// distinguishes READ_ONLY from FULL. Excluding it here means these accounts never register an
// entity they have no real role on; they fall through to ProtectedRoutes.jsx's existing
// dashboard-permission gate instead, which already redirects to the service's About page for
// anyone lacking a real dashboard-role flag (see servicesConfig[...].aboutRoute) - the read-only
// landing experience this was supposed to be, using a page that already exists.
const ENTITY_CHECK_ELIGIBLE_LEVELS = ["FULL"];

// accessControls actions that mean "this account can actually create/complete this service's
// entity setup". The setup routes are admin-tier gated (allowedRoles: ["fpoAdmin"] /
// ["businessAdmin"] in routes.js), so an account with only "R" that gets redirected into
// /X/setup is bounced straight back off that role gate to /home, which re-runs the gateway and
// re-lands on the selection grid — an infinite loop. A create ("C") or edit ("E") grant is the
// accessControls-level signal that the account is admin-tier for the entity; real fpo_admin
// accounts carry "E" without "C", so both count. "R"-only (viewer-tier) accounts skip setup and
// enter the service read-only instead.
const ENTITY_SETUP_CAPABLE_ACTIONS = ["C", "E"];

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) throw new Error("useAuth must be used within an AuthProvider");
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [accessToken, setAccessToken] = useState(null);
  const [refreshTokenValue, setRefreshTokenValue] = useState(null);
  const [normalizedRoles, setNormalizedRoles] = useState({});
  const [permissions, setPermissions] = useState({ normalPermissions: {}, accessControls: {} });
  const [userId, setUserId] = useState(null);
  const [fullUserData, setFullUserData] = useState(null);
  const [loadingUserData, setLoadingUserData] = useState(false);
  const [loading, setLoading] = useState(true);

  const [userTypeInfo, setUserTypeInfo] = useState(null);
  const [subscriptionInfo, setSubscriptionInfo] = useState(null);
  const [accessibilityRules, setAccessibilityRules] = useState({});
  const [serviceLimits, setServiceLimits] = useState({});

  // Persistent usertype switcher (cross-service access plan, Part 5). Which usertype context is
  // "active" right now - purely a display anchor for Home/dashboard scoping and the generic
  // subscription page's current-plan resolution, never a security boundary: both contexts stay
  // reachable by direct navigation regardless of which one is active, since the underlying
  // permissions are already additive. sessionStorage so it resets naturally on logout, same as
  // the tokens.
  const [activeUserTypeCode, setActiveUserTypeCodeState] = useState(
    () => sessionStorage.getItem("activeUserTypeCode") || null
  );
  const setActiveUserTypeCode = useCallback((code) => {
    setActiveUserTypeCodeState(code);
    if (code) sessionStorage.setItem("activeUserTypeCode", code);
    else sessionStorage.removeItem("activeUserTypeCode");
  }, []);
  // Login-time-only snapshot (AUTH_LOGIN_FLOW_PLAN.md §4/§6) — an early, one-shot nudge signal,
  // never re-fetched, never used as an ongoing authorization source. pendingEntitySetups above is
  // the authoritative, live check; this exists only to surface the backend's fresh-login
  // needsSetup hint (e.g. for an immediate post-login toast/redirect hint) before fullUserData
  // has loaded.
  const [loginServiceEntities, setLoginServiceEntities] = useState([]);

  // Phase 7 — pending setup users (systemAdmin only)
  const [pendingUsers, setPendingUsers] = useState([]);
  const [showPendingModal, setShowPendingModal] = useState(false);

  // Timer refs
  const logoutTimerRef = useRef(null);
  const refreshTimerRef = useRef(null);

  // Idle timeout refs
  const [showTimeoutModal, setShowTimeoutModal] = useState(false);
  const [countdown, setCountdown] = useState(0);
  const countdownTimerRef = useRef(null);
  const inactivityCheckIntervalRef = useRef(null);

  // Ref to track current modal state
  const showTimeoutModalRef = useRef(false);

  // Configuration
  const INACTIVITY_LIMIT = 8 * 60 * 1000; // 8 minutes of inactivity
  const COUNTDOWN_LIMIT = 60; // 60 seconds countdown

  // Logout function
  // options.fromOtherTab: another open tab already logged out (and ended the session on the server).
  const logout = useCallback(async (options) => {
    const fromOtherTab = options?.fromOtherTab === true;
    if (!fromOtherTab) {
      try {
        await logoutApi();
      } catch (error) {
        console.error("Logout API failed", error);
      }
    }
    // Clear all timers
    if (logoutTimerRef.current) {
      clearTimeout(logoutTimerRef.current);
      logoutTimerRef.current = null;
    }

    if (refreshTimerRef.current) {
      clearTimeout(refreshTimerRef.current);
      refreshTimerRef.current = null;
    }

    if (countdownTimerRef.current) {
      clearInterval(countdownTimerRef.current);
      countdownTimerRef.current = null;
    }

    if (inactivityCheckIntervalRef.current) {
      clearInterval(inactivityCheckIntervalRef.current);
      inactivityCheckIntervalRef.current = null;
    }

    // Clear activity tracking
    localStorage.removeItem("lastActivity");

    // Reset all states
    setAccessToken(null);
    setRefreshTokenValue(null);
    setUser(null);
    setNormalizedRoles({});
    setPermissions({});
    setUserId(null);
    setFullUserData(null);
    setUserTypeInfo(null);
    setSubscriptionInfo(null);
    setAccessibilityRules({});
    setServiceLimits({});
    setLoginServiceEntities([]);
    setShowTimeoutModal(false);
    setCountdown(0);
    showTimeoutModalRef.current = false;

    // Clear session storage
    sessionStorage.removeItem("accessToken");
    sessionStorage.removeItem("refreshToken");
    sessionStorage.removeItem("userData");

    // The login is shared by all open tabs (Utils/tabSession.js): they log out too.
    if (!fromOtherTab) shareLogout();

    // No explicit redirect here -- ProtectedRoutes.jsx already renders
    // <Navigate to="/" replace /> as soon as accessToken becomes null (set above), so this
    // clears the auth state and lets that existing guard do the redirect once, client-side.
    // A window.location.href here used to force a second, full hard reload to the same "/" on
    // top of that client-side redirect -- the double navigation/page flash reported as
    // "logout redirects, then refreshes and opens login again".
  }, []);

  // Apply fresh userData (roles + permissions) from the /authentication/token endpoint
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const applyFreshUserData = useCallback((freshData) => {
    if (!freshData) return;
    const roles = {};
    if (freshData.roles) {
      Object.keys(freshData.roles).forEach((role) => {
        if (freshData.roles[role] === true) roles[role] = true;
      });
    }
    setNormalizedRoles(roles);
    setUser(freshData);
    setPermissions(freshData.permissions || { normalPermissions: {}, accessControls: {} });
    setUserTypeInfo(freshData.userTypeCode ? {
      code: freshData.userTypeCode,
      subscriptionRequired: freshData.subscriptionRequired || false,
    } : null);
    setSubscriptionInfo(freshData.subscriptionCode ? {
      code: freshData.subscriptionCode,
      expiry: freshData.subscriptionExpiry || null,
    } : null);
    setAccessibilityRules(freshData.accessibilityRules || {});
    setServiceLimits(freshData.serviceLimits || {});
    sessionStorage.setItem("userData", JSON.stringify(freshData));
  }, []);

  // Re-fetch userData from the backend and update context (call after role changes, or when a
  // live notification signals the user's own access just changed — e.g. subscription approved).
  //
  // Bug fix (2026-08-20): this used to call applyFreshUserData() and setFullUserData()
  // sequentially, each after its own await — so a render could happen between them with fresh
  // normalizedRoles/subscriptionInfo paired against still-stale fullUserData. ProtectedRoutes'
  // subscription check reads isSubscriptionRequired() (from userTypeInfo, updated by
  // applyFreshUserData) together with hasActiveSubscription() (from fullUserData.
  // userSubscriptions, updated separately) — that mismatched intermediate render could read as
  // "subscription required, none active" and bounce the user to /home/subscription right after
  // their subscription had just been approved. Now both API calls are awaited first, and both
  // state updates are applied together at the end so no render sees a mismatched pair.
  const reloadUserData = useCallback(async () => {
    let freshData = null;
    try {
      const res = await authToken();
      freshData = res?.data?.data;
    } catch {
      // Non-critical — silently ignore
    }

    let freshFullUserData;
    let fullUserDataFetchOk = false;
    try {
      if (userId) {
        const profileRes = await getUsersId(userId);
        freshFullUserData = profileRes.data?.data?.[0] || null;
        fullUserDataFetchOk = true;
      }
    } catch {
      // Non-critical — silently ignore (keeps existing fullUserData rather than clearing it)
    }

    if (freshData) applyFreshUserData(freshData);
    if (fullUserDataFetchOk) setFullUserData(freshFullUserData);
  }, [applyFreshUserData, userId]);

  // Refresh token function
  const refreshAccessToken = useCallback(async () => {
    const storedRefreshToken = sessionStorage.getItem("refreshToken");
    try {
      if (!storedRefreshToken) {
        logout();
        return null;
      }

      const response = await refreshToken(storedRefreshToken);
      const newAccessToken = response.data?.data?.token;
      const newRefreshToken = response.data?.data?.refreshToken;

      if (!newAccessToken) {
        throw new Error("Failed to refresh token");
      }

      // Update tokens in state
      setAccessToken(newAccessToken);
      sessionStorage.setItem("accessToken", newAccessToken);

      if (newRefreshToken) {
        setRefreshTokenValue(newRefreshToken);
        sessionStorage.setItem("refreshToken", newRefreshToken);
      }
      shareTokens(newAccessToken, newRefreshToken);   // the other open tabs use the new tokens too

      // Decode new token to set up new timer
      try {
        const decodedToken = jwtDecode(newAccessToken);
        if (decodedToken.exp) {
          setupTokenRefreshTimer(decodedToken.exp);
          setupLogoutTimer(decodedToken.exp);
        }
      } catch (error) {
        // Silent error for token decoding
      }

      // Re-fetch userData so roles/permissions reflect any changes since login
      try {
        const res = await authToken();
        const freshData = res?.data?.data;
        if (freshData) applyFreshUserData(freshData);
      } catch {
        // Non-critical — keep existing userData
      }

      return newAccessToken;
    } catch (error) {
      // Another open tab may have refreshed first (its new tokens are already here): keep going.
      const sharedAccessToken = sessionStorage.getItem("accessToken");
      if (storedRefreshToken && sharedAccessToken && sessionStorage.getItem("refreshToken") !== storedRefreshToken) {
        return sharedAccessToken;
      }
      logout();
      return null;
    }
  }, [logout, applyFreshUserData]); // eslint-disable-line react-hooks/exhaustive-deps

  // Setup token refresh timer (refresh 2 minutes before expiry)
  const setupTokenRefreshTimer = useCallback((expiryTime) => {
    if (refreshTimerRef.current) clearTimeout(refreshTimerRef.current);

    // Refresh 2 minutes before token expires
    const refreshTimeMs = (expiryTime * 1000) - Date.now() - (2 * 60 * 1000);

    if (refreshTimeMs > 0) {
      refreshTimerRef.current = setTimeout(() => {
        refreshAccessToken();
      }, refreshTimeMs);
    } else {
      // Token already expired or will expire soon
      refreshAccessToken();
    }
  }, [refreshAccessToken]);

  // Setup auto logout timer when token expires
  const setupLogoutTimer = useCallback((expiryTime) => {
    if (logoutTimerRef.current) clearTimeout(logoutTimerRef.current);

    const expiresInMs = (expiryTime * 1000) - Date.now();

    if (expiresInMs > 0) {
      logoutTimerRef.current = setTimeout(() => {
        logout();
      }, expiresInMs);
    } else {
      // Token already expired
      logout();
    }
  }, [logout]);

  // Update user activity timestamp
  const updateActivity = useCallback(() => {
    // If modal is showing, don't update activity
    if (showTimeoutModalRef.current) {
      return;
    }

    // Only update activity if modal is NOT showing
    const now = Date.now().toString();
    localStorage.setItem("lastActivity", now);
  }, []);

  // Trigger idle timeout warning
  const triggerWarning = useCallback(() => {
    // Clear any existing countdown timer
    if (countdownTimerRef.current) {
      clearInterval(countdownTimerRef.current);
      countdownTimerRef.current = null;
    }

    // Set modal state and start countdown at 60
    setShowTimeoutModal(true);
    setCountdown(COUNTDOWN_LIMIT);
  }, [COUNTDOWN_LIMIT]);

  // Check for inactivity
  const checkInactivity = useCallback(() => {
    const lastActivity = localStorage.getItem("lastActivity");

    if (!lastActivity) {
      return;
    }

    const last = parseInt(lastActivity);
    const now = Date.now();
    const diff = now - last;

    // Use ref to get current modal state
    if (diff >= INACTIVITY_LIMIT && !showTimeoutModalRef.current) {
      triggerWarning();
    }
  }, [triggerWarning, INACTIVITY_LIMIT]);

  // Stay logged in handler
  const stayLoggedIn = useCallback(() => {
    // Clear the countdown timer
    if (countdownTimerRef.current) {
      clearInterval(countdownTimerRef.current);
      countdownTimerRef.current = null;
    }

    // Hide modal and reset countdown
    setShowTimeoutModal(false);
    setCountdown(0);

    // Update activity timestamp
    const now = Date.now().toString();
    localStorage.setItem("lastActivity", now);
  }, []);

  // Logout from modal handler
  const handleLogoutFromModal = useCallback(() => {
    // Clear the countdown timer
    if (countdownTimerRef.current) {
      clearInterval(countdownTimerRef.current);
      countdownTimerRef.current = null;
    }

    // Hide modal first
    setShowTimeoutModal(false);
    setCountdown(0);

    // Then call logout
    setTimeout(() => {
      logout();
    }, 100);
  }, [logout]);

  // Countdown timer useEffect
  useEffect(() => {
    if (showTimeoutModal && countdown > 0) {
      // Clear any existing timer
      if (countdownTimerRef.current) {
        clearInterval(countdownTimerRef.current);
        countdownTimerRef.current = null;
      }

      // Start new timer
      countdownTimerRef.current = setInterval(() => {
        setCountdown(prevCountdown => {
          const newCountdown = prevCountdown - 1;

          if (newCountdown <= 0) {
            clearInterval(countdownTimerRef.current);
            countdownTimerRef.current = null;

            // Logout after a small delay
            setTimeout(() => {
              logout();
            }, 100);

            return 0;
          }

          return newCountdown;
        });
      }, 1000);

      // Cleanup on unmount or when modal closes
      return () => {
        if (countdownTimerRef.current) {
          clearInterval(countdownTimerRef.current);
          countdownTimerRef.current = null;
        }
      };
    }
  }, [showTimeoutModal, countdown, logout]);

  // Sync showTimeoutModal state with ref
  useEffect(() => {
    showTimeoutModalRef.current = showTimeoutModal;
  }, [showTimeoutModal]);

  // Start/restart inactivity check interval
  const startInactivityCheck = useCallback(() => {
    // Clear existing interval
    if (inactivityCheckIntervalRef.current) {
      clearInterval(inactivityCheckIntervalRef.current);
      inactivityCheckIntervalRef.current = null;
    }

    // Start new interval
    inactivityCheckIntervalRef.current = setInterval(() => {
      checkInactivity();
    }, 10000); // Check every 10 seconds
  }, [checkInactivity]);

  // Login function
  const login = useCallback((token, userData, refreshTokenValue, serviceEntities) => {
    // Clear all existing timers
    if (logoutTimerRef.current) {
      clearTimeout(logoutTimerRef.current);
      logoutTimerRef.current = null;
    }

    if (refreshTimerRef.current) {
      clearTimeout(refreshTimerRef.current);
      refreshTimerRef.current = null;
    }

    if (countdownTimerRef.current) {
      clearInterval(countdownTimerRef.current);
      countdownTimerRef.current = null;
    }

    if (inactivityCheckIntervalRef.current) {
      clearInterval(inactivityCheckIntervalRef.current);
      inactivityCheckIntervalRef.current = null;
    }

    // Reset modal ref
    showTimeoutModalRef.current = false;

    // Set states
    setAccessToken(token);
    setUser(userData);
    setRefreshTokenValue(refreshTokenValue);

    try {
      const decodedToken = jwtDecode(token);
      setUserId(decodedToken.sub);

      // Setup token timers
      if (decodedToken.exp) {
        // Refresh 2 minutes before expiry
        const refreshTimeMs = (decodedToken.exp * 1000) - Date.now() - (2 * 60 * 1000);
        if (refreshTimeMs > 0) {
          refreshTimerRef.current = setTimeout(() => {
            refreshAccessToken();
          }, refreshTimeMs);
        }

        const expiresInMs = (decodedToken.exp * 1000) - Date.now();
        if (expiresInMs > 0) {
          logoutTimerRef.current = setTimeout(() => {
            logout();
          }, expiresInMs);
        }
      }
    } catch (error) {
      setUserId(null);
    }

    // Set normalized roles
    const roles = {};
    if (userData.roles) {
      Object.keys(userData.roles).forEach((role) => {
        if (userData.roles[role] === true) roles[role] = true;
      });
    }
    setNormalizedRoles(roles);

    // Set permissions
    setPermissions(userData.permissions || { normalPermissions: {}, accessControls: {} });

    // Phase 5 — set userType and subscription info
    setUserTypeInfo(userData.userTypeCode ? {
      code: userData.userTypeCode,
      subscriptionRequired: userData.subscriptionRequired || false,
    } : null);
    setSubscriptionInfo(userData.subscriptionCode ? {
      code: userData.subscriptionCode,
      expiry: userData.subscriptionExpiry || null,
    } : null);
    setAccessibilityRules(userData.accessibilityRules || {});
    setServiceLimits(userData.serviceLimits || {});
    setLoginServiceEntities(Array.isArray(serviceEntities) ? serviceEntities : []);
    // Phase 7 — load pending-setup users for systemAdmin on login
    const isSystemAdmin = roles.systemAdmin === true;

    if (isSystemAdmin) {
      getPendingSetupUsers()
        .then((res) => {
          const list = res?.data?.data || [];
          if (list.length > 0) {
            setPendingUsers(list);
            setShowPendingModal(true);
          }
        })
        .catch(() => { /* non-critical — swallow silently */ });
    }

    // Store tokens in sessionStorage
    sessionStorage.setItem("accessToken", token);
    sessionStorage.setItem("refreshToken", refreshTokenValue);
    sessionStorage.setItem("userData", JSON.stringify(userData));

    // Set initial activity timestamp
    const now = Date.now().toString();
    localStorage.setItem("lastActivity", now);

    // Start inactivity checking
    startInactivityCheck();

  }, [refreshAccessToken, logout, startInactivityCheck]);

  // Track user activity
  useEffect(() => {
    const activityEvents = ['mousedown', 'mousemove', 'keypress', 'scroll', 'touchstart', 'click'];

    const handleActivity = () => {
      updateActivity();
    };

    // Add event listeners
    activityEvents.forEach(event => {
      window.addEventListener(event, handleActivity);
    });

    // Listen for storage events (cross-tab sync)
    const handleStorage = (e) => {
      if (e.key === 'lastActivity') {
        updateActivity();
      }
    };
    window.addEventListener('storage', handleStorage);

    // Cleanup
    return () => {
      activityEvents.forEach(event => {
        window.removeEventListener(event, handleActivity);
      });
      window.removeEventListener('storage', handleStorage);

      // Clear timers on unmount
      if (logoutTimerRef.current) clearTimeout(logoutTimerRef.current);
      if (refreshTimerRef.current) clearTimeout(refreshTimerRef.current);
      if (countdownTimerRef.current) clearInterval(countdownTimerRef.current);
      if (inactivityCheckIntervalRef.current) clearInterval(inactivityCheckIntervalRef.current);
    };
  }, [updateActivity]);

  // Initialize auth on page load
  useEffect(() => {
    const initializeAuth = async () => {
      try {
        // A new tab has no login of its own yet: take the one of another open tab, if any (Utils/tabSession.js).
        if (!sessionStorage.getItem("accessToken")) {
          const shared = await askOtherTabsForSession();
          if (shared?.activeUserTypeCode) setActiveUserTypeCodeState(shared.activeUserTypeCode);
        }

        const token = sessionStorage.getItem("accessToken");
        const storedRefreshToken = sessionStorage.getItem("refreshToken");
        const userData = sessionStorage.getItem("userData");

        if (token && userData && storedRefreshToken) {
          let parsedUser =
            typeof userData === "string" ? JSON.parse(userData) : userData;

          setAccessToken(token);
          setUser(parsedUser);
          setRefreshTokenValue(storedRefreshToken);

          try {
            const decodedToken = jwtDecode(token);
            setUserId(decodedToken.sub);

            // Setup token timers
            if (decodedToken.exp) {
              // Refresh 2 minutes before expiry
              const refreshTimeMs = (decodedToken.exp * 1000) - Date.now() - (2 * 60 * 1000);
              if (refreshTimeMs > 0) {
                refreshTimerRef.current = setTimeout(() => {
                  refreshAccessToken();
                }, refreshTimeMs);
              }

              const expiresInMs = (decodedToken.exp * 1000) - Date.now();
              if (expiresInMs > 0) {
                logoutTimerRef.current = setTimeout(() => {
                  logout();
                }, expiresInMs);
              }
            }
          } catch (error) {
            setUserId(null);
          }

          // Set normalized roles
          const roles = {};
          if (parsedUser.roles) {
            Object.keys(parsedUser.roles).forEach((role) => {
              if (parsedUser.roles[role] === true) roles[role] = true;
            });
          }
          setNormalizedRoles(roles);
          setPermissions(parsedUser.permissions || { normalPermissions: {}, accessControls: {} });

          // Phase 5 — restore userType + subscription on page reload
          setUserTypeInfo(parsedUser.userTypeCode ? {
            code: parsedUser.userTypeCode,
            subscriptionRequired: parsedUser.subscriptionRequired || false,
          } : null);
          setSubscriptionInfo(parsedUser.subscriptionCode ? {
            code: parsedUser.subscriptionCode,
            expiry: parsedUser.subscriptionExpiry || null,
          } : null);
          setAccessibilityRules(parsedUser.accessibilityRules || {});
          setServiceLimits(parsedUser.serviceLimits || {});

          // Set initial activity timestamp
          const now = Date.now().toString();
          localStorage.setItem("lastActivity", now);

          // Start inactivity checking
          startInactivityCheck();

          // sessionStorage's userData is a snapshot from login (or the last token refresh) — an
          // accessibility rule an admin changes mid-session isn't reflected in it. Re-evaluate
          // immediately on every reload (same authToken()/applyFreshUserData() pair the silent
          // token refresh already uses) so a plain browser refresh picks up the change instead of
          // requiring a full logout/login. Fire-and-forget: the cached snapshot above already
          // rendered the page, this just corrects it a moment later if anything changed.
          authToken()
            .then((res) => {
              const freshData = res?.data?.data;
              if (freshData) applyFreshUserData(freshData);
            })
            .catch(() => {
              // Non-critical — keep the cached snapshot
            });
        }
      } catch (error) {
        sessionStorage.removeItem("accessToken");
        sessionStorage.removeItem("refreshToken");
        sessionStorage.removeItem("userData");
      } finally {
        setLoading(false);
      }
    };
    initializeAuth();
  }, [refreshAccessToken, logout, startInactivityCheck, applyFreshUserData]);

  // Keep in step with the other open tabs: their token refreshes and their logout.
  useEffect(() => {
    const stopTokens = onOtherTabTokens(({ accessToken: newAccessToken, refreshToken: newRefreshToken }) => {
      setAccessToken(newAccessToken);
      if (newRefreshToken) setRefreshTokenValue(newRefreshToken);
      try {
        const decodedToken = jwtDecode(newAccessToken);
        if (decodedToken.exp) {
          setupTokenRefreshTimer(decodedToken.exp);   // the old timers were for the replaced token
          setupLogoutTimer(decodedToken.exp);
        }
      } catch (error) {
        // Silent error for token decoding
      }
    });
    const stopLogout = onOtherTabLogout(() => logout({ fromOtherTab: true }));
    return () => {
      stopTokens();
      stopLogout();
    };
  }, [logout, setupTokenRefreshTimer, setupLogoutTimer]);

  // Fetch full user data
  useEffect(() => {
    const fetchFullUserData = async () => {
      if (!userId) return;
      setLoadingUserData(true);
      try {
        const response = await getUsersId(userId);
        setFullUserData(response.data.data[0] || null);
      } catch (error) {
        setFullUserData(null);
      } finally {
        setLoadingUserData(false);
      }
    };
    fetchFullUserData();
  }, [userId]);

  const hasServiceAccess = (serviceKey) => {
    if (normalizedRoles?.systemAdmin) return true;
    // users and admin pages are systemAdmin-only (no permission key)
    const config = servicesConfig[serviceKey];
    if (!config?.permissionKey) return false;
    const actions = permissions?.accessControls?.[config.permissionKey];
    return !!(actions && actions.length > 0);
  };

  // Dashboard-entry gate — additive to hasServiceAccess (accessControls-based), never a
  // replacement. Checks permissions.normalPermissions (Role-Creation-assigned Dashboard
  // permissions), not roles — a service's dashboardPermissions config lists every permission
  // key actually granted to any role expected to reach it (see SERVICE_DASHBOARD_NAVIGATION_PLAN.md
  // §2/§2a). No dashboardPermissions configured on a service = pass through, not deny.
  const hasAnyNormalPermission = (permissionKeys) => {
    if (normalizedRoles?.systemAdmin === true) return true;
    if (!permissionKeys || permissionKeys.length === 0) return true;
    return permissionKeys.some((key) => permissions?.normalPermissions?.[key] === true);
  };

  const hasEntityAccess = (entityKey, action = "R") => {
    const controls = permissions?.accessControls || {};
    const actions = controls[entityKey];
    if (!actions || !Array.isArray(actions)) return false;
    return actions.includes(action);
  };

  const isSubscriptionRequired = () => {
    return userTypeInfo?.subscriptionRequired === true;
  };

  // Reads status + expiryDate straight off fullUserData.userSubscriptions (populated via
  // getUsersId) — expiryDate is a real, persisted backend field set by the approval flow
  // (see PERMISSIONS_IMPLEMENTATION_PLAN.md "Future Phase — Subscription Expiry Enforcement"
  // for the live verification), never computed client-side. subscriptionInfo.expiry is dead —
  // the login/token payload has never populated it. Only PLAN-type records gate blanket route
  // access here; SERVICE-type (per-service) subscriptions remain useServiceAccess's concern.
  const hasActiveSubscription = () => {
    const subscriptions = fullUserData?.userSubscriptions || [];
    const today = new Date();
    return subscriptions.some(
      (sub) =>
        sub.subscriptionType === "PLAN" &&
        sub.status === "ACTIVE" &&
        getSubscriptionExpiryDateObject(sub) &&
        getSubscriptionExpiryDateObject(sub) >= today
    );
  };

  // Fully dynamic (AUTH_LOGIN_FLOW_PLAN.md §3-§6) — no role names or userTypeCodes. Skip entity
  // enforcement entirely for internal staff or any userType that doesn't require a subscription
  // (real data: userTypeEntity.internalUser / userTypeInfo.subscriptionRequired). Otherwise, for
  // each entity-owning service, its accessibilityRules tier decides whether the service is in
  // scope for entity setup (FULL eligible, HIDDEN/SUBSCRIBE_REQUIRED/READ_ONLY not - see
  // ENTITY_CHECK_ELIGIBLE_LEVELS above)
  // — looked up by config.serviceKey (e.g. "MY_ORG"), which is the key accessibilityRules
  // actually uses; config.frontendServiceKey ("myFpo") is a different namespace used only by
  // hasServiceAccess/accessControls, not by accessibilityRules. entityFields.some(...) then
  // checks whichever field the backend actually populated (admin-tier fpoEntity/companyEntity or
  // member-tier membershipEntity), not one hardcoded pairing. accessibilityRules is not a
  // replacement for accessControls — it only gates which services participate in this check.
  //
  // A service is only "pending setup" for accounts that can actually perform the setup — i.e.
  // hold a create/edit grant on its accessControls key (ENTITY_SETUP_CAPABLE_ACTIONS). Without
  // this, a viewer-tier account (accessControls "R" only, no entity) was redirected into the
  // admin-gated /X/setup route, bounced to /home, and re-landed on the gateway forever. Viewers
  // now skip setup and enter the service read-only.
  //
  // Returns EVERY eligible-and-missing entity-owning service, not just the first — an account can
  // legitimately be eligible-and-missing for more than one at once (e.g. accessibilityRules grants
  // FULL on two different cross-usertype services at once, neither entity registered yet). Bug
  // fix (2026-08-26): this used to `return` on the first match, so whichever
  // service happened to come first in SERVICE_ENTITY_REGISTRY's key order silently swallowed every
  // other pending service — confirmed live: an account eligible-and-missing on both MY_ORG and
  // MY_BUSINESS never got redirected into /mybusiness/setup because MY_ORG's config won the race,
  // and ProtectedRoutes only checks the ONE config it's handed against the current route. Consumers
  // must find the entry whose routePrefix matches the current route, not assume there's only one.
  const pendingEntitySetups = useMemo(() => {
    if (!fullUserData) return [];
    const skipEntitySetup =
      fullUserData.userTypeEntity?.internalUser === true ||
      userTypeInfo?.subscriptionRequired === false;
    if (skipEntitySetup) return [];

    return Object.values(SERVICE_ENTITY_REGISTRY).filter((config) => {
      const { level } = parseAccessibilityRule(accessibilityRules?.[config.serviceKey]);
      if (!ENTITY_CHECK_ELIGIBLE_LEVELS.includes(level)) return false;
      // Skip accounts that can't complete the admin-gated setup route (see comment above and
      // ENTITY_SETUP_CAPABLE_ACTIONS) — looked up by the same permissionKey hasServiceAccess uses.
      const permissionKey = servicesConfig[config.frontendServiceKey]?.permissionKey;
      const actions = permissions?.accessControls?.[permissionKey] || [];
      if (!ENTITY_SETUP_CAPABLE_ACTIONS.some((action) => actions.includes(action))) return false;
      return !config.entityFields.some(
        (field) => !!fullUserData[field] && isGenuineEntityField(config.serviceKey, field, userTypeInfo?.code)
      );
    });
  }, [fullUserData, userTypeInfo, accessibilityRules, permissions]);

  // A usertype "context" this login genuinely holds - the account's own base usertype, plus any
  // OTHER SERVICE_ENTITY_REGISTRY service whose home usertype differs from that base AND whose
  // entity already exists (registration completed - a second usertype actually held, not just
  // eligible-and-pending, which is pendingEntitySetups' concern instead).
  const formatUserTypeLabel = (code) =>
    code ? code.charAt(0) + code.slice(1).toLowerCase() : code;

  // Returns the full subscription row (plan + expiryDate/status), not just the nested plan - see
  // findPlanForService below for the plan-only shape Header.js/UsertypeGate.jsx already depend on.
  const findSubscriptionForService = useCallback(
    (serviceKey) => {
      const subs = fullUserData?.userSubscriptions || [];
      const now = new Date();
      const isActivePlan = (s) =>
        s.status === "ACTIVE" &&
        s.subscriptionType === "PLAN" &&
        (!s.expiryDate || new Date(`${s.expiryDate}T00:00:00`) >= now);
      // A row tagged for this exact service takes precedence (see
      // AccessEvaluationServiceImpl.resolveTaggedSubscriptionCodes) - falls back to the account's
      // general/untagged plan when this service has no tagged row of its own.
      const tagged = subs.find((s) => isActivePlan(s) && s.serviceKey === serviceKey);
      if (tagged) return tagged;
      const general = subs.find((s) => isActivePlan(s) && !s.serviceKey);
      return general || null;
    },
    [fullUserData]
  );

  const findPlanForService = useCallback(
    (serviceKey) => findSubscriptionForService(serviceKey)?.plan || null,
    [findSubscriptionForService]
  );

  const userTypeContexts = useMemo(() => {
    if (!fullUserData) return [];
    const baseUserTypeCode = fullUserData.userTypeEntity?.code || null;
    const contexts = [];

    if (baseUserTypeCode) {
      // A member's own base usertype code (FPO_MEMBER/BUSINESS_MEMBER) never matches any
      // registry entry's homeUserTypeCode (those are the admin-tier INSTITUTIONAL/INDIVIDUAL
      // codes only) - without the MEMBER_HOME_SERVICE_KEY fallback, homeConfig came back
      // undefined for every member, so this "home" context's own serviceKey/frontendServiceKey
      // was always null. UsertypeGate.jsx and the header switcher both resolve "is this context
      // actually accessible" via that serviceKey - with it null, a member's own, already-active
      // identity showed as "Setup Required" the moment they held a second context to compare it
      // against (previously invisible: a single-context account never renders that comparison at
      // all). Confirmed live.
      const homeConfig = Object.values(SERVICE_ENTITY_REGISTRY).find(
        (config) => config.homeUserTypeCode === baseUserTypeCode
      ) || SERVICE_ENTITY_REGISTRY[MEMBER_HOME_SERVICE_KEY[baseUserTypeCode]];
      contexts.push({
        userTypeCode: baseUserTypeCode,
        label: fullUserData.userTypeEntity?.name || formatUserTypeLabel(baseUserTypeCode),
        serviceKey: homeConfig?.serviceKey || null,
        serviceLabel: homeConfig?.label || null,
        overviewRoute: homeConfig?.overviewRoute || "/home",
        plan: homeConfig ? findPlanForService(homeConfig.serviceKey) : findPlanForService(null),
        subscription: homeConfig
          ? findSubscriptionForService(homeConfig.serviceKey)
          : findSubscriptionForService(null),
      });
    }

    // A second context is pushed for any OTHER SERVICE_ENTITY_REGISTRY service the account
    // genuinely, independently holds an admin-tier entity for (fpoEntity/companyEntity) -
    // regardless of the account's own base usertype. This used to be gated behind
    // `isMultiIdentityCapableUserType` (only ran this check at all when the base usertype was
    // itself INSTITUTIONAL/INDIVIDUAL), on the reasoning that a member "belongs to exactly one
    // tenant" and can never hold a second identity - true for a plain member, but not for one who
    // separately completed their OWN full registration for the other service (e.g. an FPO member
    // who also registered their own MyBusiness company - a real, supported flow: see
    // CompanySetupPage.jsx/mybusiness's CompanyServiceImpl). Such an account genuinely holds a
    // companyEntity of its own, but its base usertype code (FPO_MEMBER) never equals any
    // registry entry's homeUserTypeCode, so the old gate silently kept the switcher hidden and
    // the account stuck showing only the non-interactive single-usertype badge. Confirmed live.
    // The `adminEntityFields` filter below (excluding "membershipEntity") is what actually
    // guards against the false positive the old gate was protecting against - every member
    // genuinely holds a membershipEntity for their own tenant, which is not evidence of a real
    // second identity - so dropping the outer gate does not reopen that hole.
    Object.values(SERVICE_ENTITY_REGISTRY).forEach((config) => {
      if (config.homeUserTypeCode === baseUserTypeCode) return;
      const adminEntityFields = config.entityFields.filter((field) => field !== "membershipEntity");
      const hasEntity = adminEntityFields.some((field) => !!fullUserData[field]);
      if (!hasEntity) return;
      contexts.push({
        userTypeCode: config.homeUserTypeCode,
        label: formatUserTypeLabel(config.homeUserTypeCode),
        serviceKey: config.serviceKey,
        serviceLabel: config.label,
        overviewRoute: config.overviewRoute,
        plan: findPlanForService(config.serviceKey),
        subscription: findSubscriptionForService(config.serviceKey),
      });
    });

    return contexts;
  }, [fullUserData, findPlanForService, findSubscriptionForService]);

  // Default the active context to the account's base usertype the first time contexts become
  // available - never overrides an explicit choice already in sessionStorage.
  useEffect(() => {
    if (!activeUserTypeCode && userTypeContexts.length > 0) {
      setActiveUserTypeCode(userTypeContexts[0].userTypeCode);
    }
  }, [activeUserTypeCode, userTypeContexts, setActiveUserTypeCode]);

  const activeContext = useMemo(
    () =>
      userTypeContexts.find((c) => c.userTypeCode === activeUserTypeCode) ||
      userTypeContexts[0] ||
      null,
    [userTypeContexts, activeUserTypeCode]
  );

  const value = useMemo(
    () => ({
      user,
      accessToken,
      normalizedRoles,
      permissions,
      userId,
      fullUserData,
      loadingUserData,
      loading,
      showTimeoutModal,
      countdown,
      stayLoggedIn,
      handleLogoutFromModal,
      updateActivity,
      login,
      logout,
      refreshAccessToken,
      hasServiceAccess,
      hasAnyNormalPermission,
      hasEntityAccess,
      userTypeInfo,
      setUserTypeInfo,
      subscriptionInfo,
      accessibilityRules,
      serviceLimits,
      isSubscriptionRequired,
      hasActiveSubscription,
      // Phase 7 — pending setup modal
      pendingUsers,
      showPendingModal,
      setShowPendingModal,
      reloadUserData,
      refreshUser: reloadUserData,
      pendingEntitySetups,
      loginServiceEntities,
      userTypeContexts,
      activeContext,
      activeUserTypeCode,
      setActiveUserTypeCode,
      isTokenExpired: () => {
        if (!accessToken) return true;
        try {
          const decoded = jwtDecode(accessToken);
          const currentTime = Date.now() / 1000;
          return !decoded?.exp || decoded.exp < currentTime;
        } catch {
          return true;
        }
      },
    }),
    [
      user,
      accessToken,
      normalizedRoles,
      permissions,
      userId,
      fullUserData,
      loadingUserData,
      loading,
      showTimeoutModal,
      countdown,
      stayLoggedIn,
      handleLogoutFromModal,
      updateActivity,
      login,
      logout,
      refreshAccessToken,
      reloadUserData,
      hasServiceAccess,
      hasAnyNormalPermission,
      hasEntityAccess,
      userTypeInfo,
      setUserTypeInfo,
      subscriptionInfo,
      accessibilityRules,
      serviceLimits,
      pendingUsers,
      showPendingModal,
      setShowPendingModal,
      pendingEntitySetups,
      loginServiceEntities,
      userTypeContexts,
      activeContext,
      activeUserTypeCode,
      setActiveUserTypeCode,
    ]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
